import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { formIsValid } from "utils/handleValidation";
import { useStickyState } from "utils/handleStickyState";
import axios from "axios";
import { useMsal, useAccount } from "@azure/msal-react";
import { callApiWithToken } from "fetch";
import { protectedResources } from "authConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import io from "socket.io-client";

const ApiUrl = process.env.REACT_APP_SERVER_URL;
const socket = io.connect(ApiUrl);

export const FormDataContext = React.createContext({});

function FormDataProvider({ children }) {
  const currentJob =
    window.location.pathname.split("/")[2]?.toLowerCase() || null;
  const [jobType, setJobType] = useState(currentJob);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState([]);
  const [dataFetched, setDataFetched] = useState(false);
  const [age, setAge] = useStickyState(0, "age");
  const [employment, setEmployment] = useState({});
  const [userName, setUserName] = useState([]);
  const [errors, setErrors] = useStickyState([], "Errors");
  const [currentStep, setCurrentStep] = useStickyState(0, "currentStep");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [validatedSteps, setValidatedSteps] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
  });
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [dynamicFieldsList, setDynamicFieldsList] = useStickyState(
    {
      graver: {
        secondary_workplace: 0,
        purpose_salary: 1,
      },
      gartner: {
        secondary_workplace: 0,
        wage_supplement_quality: 0,
        wage_supplement_function: 0,
        purpose_salary: 1,
      },
      gartneriarbejder: {
        secondary_workplace: 0,
        wage_supplement_quality: 0,
        wage_supplement_function: 0,
        purpose_salary: 1,
      },
      kirketjener: {
        secondary_workplace: 0,
        purpose_salary: 1,
      },
      hk: {
        secondary_workplace: 0,
        wage_supplement_quality: 0,
        wage_supplement_function: 0,
        purpose_salary: 1,
      },
      individuel: {
        secondary_workplace: 0,
        wage_supplement_quality: 0,
        wage_supplement_function: 0,
        purpose_salary: 1,
      },
    },
    "dynamicFieldsList"
  );

  //Connect to Socket.io
  useEffect(() => {
    if (userName && currentOrganization.length > 0) {
      socket.current = io(ApiUrl);
      const userInfo = [userName, currentOrganization[1]];
      socket.current.emit("new-user-add", userInfo);
      socket.current.on("get-users", (users) => {
        setOnlineUsers(users);
      });
    }
  }, [userName, currentOrganization]);

  useEffect(() => {
    if (account && inProgress === "none") {
      setUserName(account.name);
    }
  }, [account, inProgress]);

  useEffect(() => {
    if (userName != "") {
      if (!socket.current) {
        socket.current = io(ApiUrl);
      }
      socket.current.emit("update-user", jobType);
    }
  }, [jobType]);

  useEffect(() => {
    if (account && inProgress === "none" && organizations.length === 0) {
      const userId = process.env.REACT_APP_USER_ID
        ? process.env.REACT_APP_USER_ID
        : account.username.split("@")[0];
      instance
        .acquireTokenSilent({
          scopes: protectedResources.getOrganization.scopes,
          account: account,
        })
        .then((response) => {
          callApiWithToken(
            response.accessToken,
            `${protectedResources.getOrganization.endpoint}${userId}`,
            "GET"
          ).then((response) => {
            updateOrganizations(response);
          });
        })
        .catch((error) => {
          // in case if silent token acquisition fails, fallback to an interactive method
          if (error instanceof InteractionRequiredAuthError) {
            if (account && inProgress === "none") {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.getOrganization.scopes,
                })
                .then((response) => {
                  callApiWithToken(
                    response.accessToken,
                    `${protectedResources.getOrganization.endpoint}${userId}`,
                    "GET"
                  ).then((response) => {
                    updateOrganizations(response);
                  });
                })
                .catch((error) => console.log(error));
            }
          }
        });
    }
  }, [account, inProgress, instance]);

  const updateOrganizations = (organizations) => {
    if (organizations.length > 0) {
      setOrganizations(organizations);
      setDataFetched(true);
    }
    if (organizations.length === 0) alert("Ingen relevante data fundet");
    setCurrentOrganization(organizations[0] ?? []);
  };

  const updateCurrentOrganization = (organization) => {
    setCurrentOrganization(organization);
  };

  const emptyFieldsCurrentStep = () => {
    const updated = { ...employment };
    const myArray = Object.entries(updated);
    myArray.map((item) => {
      if (item[1].step === currentStep + 1) {
        if (
          item[0] === "secondary_workplace" ||
          item[0] === "purpose_salary" ||
          item[0] === "wage_supplement_quality" ||
          item[0] === "wage_supplement_function" ||
          item[0] === "workSchedule"
        ) {
          updated[item[0]].value = [];
        } else {
          updated[item[0]].value = "";
        }
        updated[item[0]].isValid = false;
      }
    });

    setEmployment(updated);
    sendDataToApi(updated);

    const currentJobType = dynamicFieldsList[jobType];
    const updatedFieldList = {
      [jobType]: {
        ...currentJobType,
        secondary_workplace: 1,
      },
    };

    const updateList = { ...dynamicFieldsList, ...updatedFieldList };
    setDynamicFieldsList(updateList);
  };

  const updateDynamicFieldsList = (fieldName, modifier) => {
    const currentJobType = dynamicFieldsList[jobType];
    const updated = {
      [jobType]: {
        ...currentJobType,
        [fieldName]: dynamicFieldsList[jobType][fieldName] + modifier,
      },
    };

    const updateList = { ...dynamicFieldsList, ...updated };
    setDynamicFieldsList(updateList);
  };

  const [numberOfValidFields, setNumberOfValidFields] = useState({
    0: 0,
    1: 0,
  });

  const updateStep = (newStep) => {
    setCurrentStep(newStep);
  };

  const removeErrorMsg = (name) => {
    setErrors(errors.filter((item) => item.name !== name));
  };

  const updateEmployment = (employment) => {
    setEmployment(employment);
  };

  const sendDataToApi = (fields) => {
    const data = {
      jobType: jobType,
      fields: fields,
      organization: currentOrganization[0],
    };
    if (account && inProgress === "none") {
      instance
        .acquireTokenSilent({
          scopes: protectedResources.updateEmployment.scopes,
          account: account,
        })
        .then((response) => {
          callApiWithToken(
            response.accessToken,
            `${protectedResources.updateEmployment.endpoint}`,
            "POST",
            data
          ).then(() => {
            console.log("data saved");
          });
        })
        .catch((error) => {
          // in case if silent token acquisition fails, fallback to an interactive method
          if (error instanceof InteractionRequiredAuthError) {
            if (account && inProgress === "none") {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.updateEmployment.scopes,
                })
                .then((response) => {
                  callApiWithToken(
                    response.accessToken,
                    `${protectedResources.updateEmployment.endpoint}`,
                    "POST",
                    data
                  ).then(() => {
                    console.log("data saved");
                  });
                })
                .catch((error) => console.log(error));
            }
          }
        });
    }
  };

  const archiveEmploymentInDb = () => {
    const data = {
      jobType: jobType,
      organization: currentOrganization[0],
    };
    if (account && inProgress === "none") {
      instance
        .acquireTokenSilent({
          scopes: protectedResources.archiveEmployment.scopes,
          account: account,
        })
        .then((response) => {
          callApiWithToken(
            response.accessToken,
            `${protectedResources.archiveEmployment.endpoint}`,
            "POST",
            data
          ).then(() => {
            console.log("employment archived");
          });
        })
        .catch((error) => {
          // in case if silent token acquisition fails, fallback to an interactive method
          if (error instanceof InteractionRequiredAuthError) {
            if (account && inProgress === "none") {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.archiveEmployment.scopes,
                })
                .then((response) => {
                  callApiWithToken(
                    response.accessToken,
                    `${protectedResources.archiveEmployment.endpoint}`,
                    "POST",
                    data
                  ).then(() => {
                    console.log("employment archived");
                  });
                })
                .catch((error) => console.log(error));
            }
          }
        });
    }
  };

  const archiveEmployment = () => {
    setEmployment({});
    setValidatedSteps({});
  };

  const updateEmploymentField = (
    step,
    e,
    key,
    extendedRequired = false,
    validationRequired = true
  ) => {
    const value = e.target ? e.target.value : e;
    const event = e.target ? e : { target: { name: key, value: e } };
    const isValid = validationRequired ? checkField(event) : true;

    const updatedEmploymentField = {
      [key]: {
        value: value,
        isValid: isValid,
        step: step,
        extendedRequired: extendedRequired,
        validationRequired: validationRequired,
      },
    };
    let updatedEmployment = { ...employment, ...updatedEmploymentField };
    let clearedFields;

    if (updateNestedFields(key, e).length) {
      // Reset unmounted fields
      updateNestedFields(key, e).forEach((field) => {
        updatedEmployment[field].value = "";
        updatedEmployment[field].isValid = false;
        clearedFields = {
          ...clearedFields,
          ...{
            [field]: employment[field],
          },
        };
      });
    }

    const updatedEmploymentFields = {
      ...updatedEmploymentField,
      ...clearedFields,
    };

    setEmployment(updatedEmployment);
    if (isValid || key === "purpose_salary")
      sendDataToApi(updatedEmploymentFields);
  };

  const validateField = (e) => {
    const fieldName = e.target ? e.target.name : e;

    removeErrorMsg(fieldName);
    const currentNumberOfValidFields = numberOfValidFields[currentStep] || 0;
    let updatedNumberOfValidFields = currentNumberOfValidFields;

    if (e.target && !formIsValid(e, updateErrorsData, calculateAge)) {
      if (currentNumberOfValidFields > 0) {
        updatedNumberOfValidFields = currentNumberOfValidFields - 1;
      }
    } else {
      updatedNumberOfValidFields = currentNumberOfValidFields + 1;
    }

    setNumberOfValidFields({
      ...numberOfValidFields,
      [currentStep]: updatedNumberOfValidFields,
    });
  };

  const updateErrorsData = (errObj) => {
    setErrors([...errors, errObj]);
  };

  function calculate_age(dob) {
    var diff_ms = Date.now() - dob.getTime();
    var age_dt = new Date(diff_ms);

    return Math.abs(age_dt.getUTCFullYear() - 1970);
  }

  const calculateAge = (birthday) => {
    const day = birthday.substring(0, 2);
    const month = birthday.substring(2, 4) - 1;
    const year = birthday.substring(4, 6);
    let fullyear;
    if (year > 20 && year < 100) {
      fullyear = "19" + year;
    } else {
      fullyear = "20" + year;
    }
    let candidateBirthday = new Date(fullyear, month, day, 0, 0, 0);
    setAge(calculate_age(candidateBirthday));
  };

  const checkField = (e) => {
    let isValid = true;
    if (e.target && !formIsValid(e, updateErrorsData)) {
      isValid = false;
    }

    return isValid;
  };

  const updateNestedFields = (name, e) => {
    const fieldsImpacted = [];
    const value = e.target ? e.target.value : e;
    Object.entries(employment).map((obj) => {
      if (
        obj[1]?.extendedRequired?.requiredField === name &&
        obj[1]?.extendedRequired?.requiredValue !== value
      ) {
        const fieldName = obj[0];
        fieldsImpacted.push(fieldName);
      }
    });

    return fieldsImpacted;
  };

  async function getCvrData(cvr) {
    const response = await axios
      .get("https://cvrapi.dk/api", {
        params: { search: cvr, country: "dk" },
      })
      .catch(() => {
        return false;
      });
    if (response.data) {
      return response.data;
    } else {
      alert(
        "Der kunne ikke findes nogle oplysninger ud fra det indtastede CVR nr."
      );
    }
  }

  const updateEmployeeDataFromCvrApi = () => {
    const step = 1;
    const extendedRequired = false;
    const validationRequired = true;
    const cvr = employment.employerCVR.value;

    getCvrData(cvr).then((data) => {
      const updatedEmployment = {
        employerName: {
          step,
          value: data?.name,
          isValid: true,
          extendedRequired,
          validationRequired,
        },
        employerAddress: {
          step,
          value: data?.address,
          isValid: true,
          extendedRequired,
          validationRequired,
        },
        employerCity: {
          step,
          value: data?.city,
          isValid: true,
          extendedRequired,
          validationRequired,
        },
        employerZipcode: {
          step,
          value: data?.zipcode,
          isValid: true,
          extendedRequired,
          validationRequired,
        },
      };
      const updateEmployment = { ...employment, ...updatedEmployment };
      setEmployment(updateEmployment);
      sendDataToApi(updatedEmployment);
    });
  };

  return (
    <FormDataContext.Provider
      value={{
        employment,
        organizations,
        currentOrganization,
        updateCurrentOrganization,
        updateEmployment,
        updateEmploymentField,
        age,
        errors,
        updateStep,
        numberOfValidFields,
        validateField,
        currentStep,
        validatedSteps,
        setValidatedSteps,
        jobType,
        setJobType,
        dynamicFieldsList,
        updateDynamicFieldsList,
        emptyFieldsCurrentStep,
        archiveEmployment,
        updateEmployeeDataFromCvrApi,
        archiveEmploymentInDb,
        dataFetched,
        onlineUsers,
        userName,
      }}
    >
      {children}
    </FormDataContext.Provider>
  );
}

FormDataProvider.propTypes = {
  children: PropTypes.object,
};

export default FormDataProvider;
